import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

/**
 * Day.js, configured once.
 *
 * Day.js plugins are global side effects — calling `.fromNow()` without having
 * registered `relativeTime` *somewhere* throws at runtime, and it only throws on
 * the code path that uses it. So every module imports from here rather than from
 * 'dayjs' directly, and the plugins are guaranteed to be registered.
 */
dayjs.extend(relativeTime); // .fromNow(), .from()
dayjs.extend(calendar); // "Tomorrow at 14:30"
dayjs.extend(localizedFormat); // .format('LLL')
dayjs.extend(utc);

/**
 * How the queue renders a scheduled time.
 *
 * Absolute timestamps ("14 Mar 2026, 09:00") are precise but make you do mental
 * arithmetic to answer the only question that matters: is this soon? Calendar
 * time answers it directly for the near future and falls back to an absolute
 * date once "next Tuesday" stops being meaningful.
 */
export function formatScheduledAt(iso: string | null): string {
  if (!iso) return '—';

  return dayjs(iso).calendar(null, {
    sameDay: '[Today at] HH:mm',
    nextDay: '[Tomorrow at] HH:mm',
    nextWeek: 'dddd [at] HH:mm',
    lastDay: '[Yesterday at] HH:mm',
    lastWeek: '[Last] dddd [at] HH:mm',
    sameElse: 'D MMM YYYY, HH:mm',
  });
}

/** "in 3 hours" / "2 days ago". For the secondary line under a timestamp. */
export function formatRelative(iso: string | null): string {
  return iso ? dayjs(iso).fromNow() : '';
}

export { dayjs };
